import clsx from "clsx";
import React from "react";
import {
  Card,
  CardProps,
  CardActionArea,
  CardHeader,
  Grid2,
  useTheme,
  useMediaQuery,
  IconButton,
  MenuItem,
  Menu,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  bindMenu,
  bindToggle,
  usePopupState,
} from "material-ui-popup-state/hooks";
import { ArrowForwardRounded, MoreHoriz } from "@mui/icons-material";

import { COACH_PROGRAMS_PUBLISHED_ROUTE } from "../../routes/routes";
import { useNavigate } from "react-router-dom";
import { ProgramFolderDto } from "@growth-machine-llc/stridist-api-client";

const useStyles = makeStyles((theme) => ({
  root: {},

  actionArea: {
    color: theme.palette.text.primary,
    padding: theme.spacing(2, 3),
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start",

    "& .MuiCardHeader-action": {
      margin: "unset",
    },
  },

  header: {
    padding: 0,
    width: "100%",

    "& .MuiCardHeader-content": {
      overflow: "hidden",
    },
  },

  title: {
    fontSize: "1rem",
    textOverflow: "ellipsis",
  },
}));

export interface ProgramFolderCardProps extends CardProps {
  folder: ProgramFolderDto;
  onRenameClick?: () => void;
  onDeleteClick?: () => void;
}

export function ProgramFolderCard(props: ProgramFolderCardProps) {
  const navigate = useNavigate();
  const { onRenameClick, onDeleteClick, className, folder, ...other } = props;
  const s = useStyles();
  const { breakpoints } = useTheme();
  const mdUp = useMediaQuery(breakpoints.up("md"));

  const folderId = folder.id;
  const name = folder.name;
  const link = `${COACH_PROGRAMS_PUBLISHED_ROUTE}?folder=${folderId}`;

  const handleCardClick = React.useCallback(
    (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => navigate(link),
    [link],
  );

  const actionsMenuState = usePopupState({
    variant: "popover",
    popupId: "actions-menu",
  });
  const actionsMenuToggle = bindToggle(actionsMenuState);

  const handleActionsMenuToggle = React.useCallback(
    (event) => {
      event.stopPropagation();
      actionsMenuToggle.onClick(event);
    },
    [actionsMenuToggle],
  );

  const actionsMenuButton = (
    <IconButton
      {...actionsMenuToggle}
      onClick={handleActionsMenuToggle}
      size="large"
    >
      <MoreHoriz />
    </IconButton>
  );

  return (
    <Card className={clsx(s.root, className)} {...other}>
      <CardActionArea className={clsx(s.actionArea)} onClick={handleCardClick}>
        <CardHeader
          className={clsx(s.header)}
          title={name}
          titleTypographyProps={{
            className: s.title,
            fontSize: "1rem",
            noWrap: true,
            color: "secondary",
          }}
          action={
            <Grid2
              container
              sx={{
                alignItems: "center",
              }}
            >
              {actionsMenuButton}
              {mdUp && <ArrowForwardRounded />}
            </Grid2>
          }
        />
      </CardActionArea>
      <Menu {...bindMenu(actionsMenuState)} keepMounted>
        <MenuItem
          onClick={() => {
            actionsMenuState.close();
            onRenameClick?.();
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            actionsMenuState.close();
            onDeleteClick?.();
          }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Card>
  );
}
